/* crm-sales-workspace.jsx — Sales Workspace daily cockpit for Phase 3
   Provides: SalesWorkspaceView
   Self-registers on window. */

const { useState, useEffect, useMemo, useRef, useCallback } = React;

/* ── helpers ── */
const fmtMoney = (n) => '$' + (n >= 1_000_000 ? (n / 1_000_000).toFixed(1) + 'M' : n >= 1_000 ? (n / 1_000).toFixed(0) + 'K' : Math.round(n || 0));
const isToday  = (ds) => { if (!ds) return false; const t = new Date(ds); const n = new Date(); return t.getFullYear() === n.getFullYear() && t.getMonth() === n.getMonth() && t.getDate() === n.getDate(); };
const isPast   = (ds) => { if (!ds) return false; const t = new Date(ds); t.setHours(23, 59, 59, 999); return t < new Date(); };
const isThisWeek = (ds) => {
  if (!ds) return false;
  const t = new Date(ds);
  const now = new Date();
  const sun = new Date(now); sun.setDate(now.getDate() - now.getDay()); sun.setHours(0,0,0,0);
  const sat = new Date(sun); sat.setDate(sun.getDate() + 6); sat.setHours(23,59,59,999);
  return t >= sun && t <= sat;
};

/* LocalStorage helpers */
const readLS = (key, fallback) => { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch { return fallback; } };

/* ── Stat Card ── */
const StatCard = ({ icon, label, value, color }) => (
  <div style={{ background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 140 }}>
    <div style={{ width: 36, height: 36, borderRadius: 8, background: (color || 'var(--nx-accent)') + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <NexusIcon name={icon} size={16} style={{ color: color || 'var(--nx-accent)' }} />
    </div>
    <div>
      <div style={{ fontWeight: 800, fontSize: 18, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 2 }}>{label}</div>
    </div>
  </div>
);

/* ── Section Header ── */
const SectionHead = ({ icon, title, count }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
    <NexusIcon name={icon} size={14} style={{ color: 'var(--nx-accent)' }} />
    <span style={{ fontWeight: 700, fontSize: 13 }}>{title}</span>
    {count != null && (
      <span style={{ fontSize: 11, background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)', borderRadius: 10, padding: '1px 7px', fontWeight: 600 }}>{count}</span>
    )}
  </div>
);

/* ══════════════════════════════════════════════════════════════
   SALES WORKSPACE VIEW
   ══════════════════════════════════════════════════════════════ */
const SalesWorkspaceView = () => {
  const store = useStore();
  const toast = useToast();
  const me    = window.__nx_auth?.user?.name;

  /* ── Recent + Favorites from LocalStorage ── */
  const [recent,    setRecent]    = useState(() => readLS('__nx_recent', []));
  const [favorites, setFavorites] = useState(() => readLS('__nx_favorites', []));

  /* Refresh LS on mount */
  useEffect(() => {
    setRecent(readLS('__nx_recent', []));
    setFavorites(readLS('__nx_favorites', []));
  }, []);

  /* ── Derived data ── */
  const myDeals = useMemo(() =>
    (store.deals || [])
      .filter(d => {
        const stage = (store.pipeline || []).find(s => s.id === d.stageId);
        return d.owner === me && stage && !/\b(won|lost)\b/i.test(stage.name);
      })
      .sort((a, b) => (b.value || 0) - (a.value || 0))
      .slice(0, 5),
    [store.deals, store.pipeline, me]
  );

  const todayTasks = useMemo(() =>
    (store.tasks || []).filter(t => t.status !== 'DONE' && t.status !== 'COMPLETED' && t.completedAt == null && isToday(t.dueDate) && (t.assignedTo === me || t.owner === me)),
    [store.tasks, me]
  );

  const overdueTasks = useMemo(() =>
    (store.tasks || []).filter(t => t.status !== 'DONE' && t.status !== 'COMPLETED' && t.completedAt == null && isPast(t.dueDate) && !isToday(t.dueDate) && (t.assignedTo === me || t.owner === me)),
    [store.tasks, me]
  );

  const todayMeetings = useMemo(() =>
    (store.activity || []).filter(a => a.type === 'meeting' && isToday(a.date || a.createdAt) && (a.owner === me || a.who === me)),
    [store.activity, me]
  );

  const followups = useMemo(() => {
    return (store.activity || []).filter(a => {
      const isFollowUpType = a.type === 'EMAIL' || a.type === 'CALL' || a.type === 'MEETING';
      const recent = a.createdAt && (Date.now() - new Date(a.createdAt).getTime()) < 7 * 86400000;
      return isFollowUpType && recent;
    }).slice(0, 10);
  }, [store.activity, me]);

  const closingThisWeek = useMemo(() =>
    (store.deals || [])
      .filter(d => d.owner === me && isThisWeek(d.closeDate))
      .reduce((s, d) => s + (d.value || 0), 0),
    [store.deals, me]
  );

  /* ── Toggle task done ── */
  const toggleTask = (t) => {
    store.update('tasks', t.id, { status: t.status === 'DONE' ? 'OPEN' : 'DONE', completedAt: t.status !== 'DONE' ? new Date().toISOString() : null });
    if (t.status !== 'DONE') toast({ tone: 'success', icon: 'CheckCircle', title: 'Task marked complete' });
  };

  /* ── Stage pill helper ── */
  const stagePill = (stageId) => {
    const s = (store.pipeline || []).find(x => x.id === stageId);
    return s ? <Pill tone="blue">{s.name}</Pill> : null;
  };

  /* ── Record type icon ── */
  const recTypeIcon = (type) => ({ deal: 'Target', person: 'User', company: 'Building2', task: 'CheckSquare', note: 'StickyNote' }[type] || 'File');

  return (
    <div style={{ padding: 'var(--space-5)', maxWidth: 1200, margin: '0 auto' }}>

      {/* ── Page title ── */}
      <div style={{ marginBottom: 'var(--space-5)' }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>
          Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}{me ? ', ' + me.split(' ')[0] : ''}
        </h1>
        <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', margin: '4px 0 0' }}>
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
        </p>
      </div>

      {/* ── Stats row ── */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 'var(--space-5)' }}>
        <StatCard icon="Calendar"   label="Meetings today"        value={todayMeetings.length}          color="var(--nx-accent)" />
        <StatCard icon="CheckSquare" label="Tasks due today"       value={todayTasks.length}             color="#10B981" />
        <StatCard icon="AlertCircle" label="Overdue items"         value={overdueTasks.length}           color="#EF4444" />
        <StatCard icon="TrendingUp" label="Closing this week"      value={fmtMoney(closingThisWeek)}     color="#8B5CF6" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* ── LEFT COLUMN ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* My Pipeline */}
          <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: '1px solid var(--nx-border)' }}>
            <SectionHead icon="Target" title="My Pipeline" count={myDeals.length} />
            {myDeals.length === 0 ? (
              <EmptyState icon="Target" title="No open deals" desc="You have no active deals assigned to you." compact />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {myDeals.map(d => {
                  const co = (store.companies || []).find(c => c.id === d.companyId);
                  return (
                    <button key={d.id} className="nx-prelated-row" style={{ background: 'var(--nx-surface)', borderRadius: 8 }} onClick={() => store.openDrawer(d)}>
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--nx-accent-tint)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <NexusIcon name="Target" size={14} style={{ color: 'var(--nx-accent)' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                        <div style={{ fontWeight: 600, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title}</div>
                        <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                          {co && <span>{co.name}</span>}
                          {stagePill(d.stageId)}
                          {d.age != null && <span>{d.age}d old</span>}
                        </div>
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--nx-accent)', flexShrink: 0 }}>{fmtMoney(d.value)}</div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Today's Agenda */}
          <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: '1px solid var(--nx-border)' }}>
            <SectionHead icon="Calendar" title="Today's Agenda" count={todayTasks.length + todayMeetings.length} />
            {todayTasks.length === 0 && todayMeetings.length === 0 ? (
              <EmptyState icon="Calendar" title="Clear schedule" desc="No tasks or meetings due today." compact />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {todayMeetings.map(a => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', background: 'var(--nx-surface)', borderRadius: 6 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 6, background: '#EDF4FD', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <NexusIcon name="Calendar" size={12} style={{ color: '#125AB0' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.what || a.detail || 'Meeting'}</div>
                      <div style={{ fontSize: 10, color: 'var(--nx-text-muted)' }}>Meeting · {a.time || 'Today'}</div>
                    </div>
                  </div>
                ))}
                {todayTasks.map(t => (
                  <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', background: 'var(--nx-surface)', borderRadius: 6 }}>
                    <button
                      type="button"
                      onClick={() => toggleTask(t)}
                      style={{ width: 18, height: 18, borderRadius: 4, border: '2px solid var(--nx-border)', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
                    >
                      {(t.status === 'DONE' || t.completedAt) && <NexusIcon name="Check" size={10} />}
                    </button>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
                      <div style={{ fontSize: 10, color: 'var(--nx-text-muted)' }}>{t.type || 'Task'}{t.dealTitle ? ' · ' + t.dealTitle : ''}</div>
                    </div>
                    <Pill tone={t.priority === 'High' ? 'red' : t.priority === 'Medium' ? 'amber' : 'gray'}>{t.priority}</Pill>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Overdue Items */}
          <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: overdueTasks.length > 0 ? '1px solid #FCA5A5' : '1px solid var(--nx-border)' }}>
            <SectionHead icon="AlertCircle" title="Overdue" count={overdueTasks.length} />
            {overdueTasks.length === 0 ? (
              <EmptyState icon="CheckCircle" title="Nothing overdue" desc="All caught up." compact />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {overdueTasks.slice(0, 6).map(t => (
                  <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', background: 'var(--nx-surface)', borderRadius: 6 }}>
                    <button
                      type="button"
                      onClick={() => toggleTask(t)}
                      style={{ width: 18, height: 18, borderRadius: 4, border: '2px solid #EF4444', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>{t.title}</div>
                      <div style={{ fontSize: 10, color: '#EF4444', fontWeight: 600 }}>Due: {t.dueDate ? new Date(t.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT COLUMN ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Quick Create */}
          <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: '1px solid var(--nx-border)' }}>
            <SectionHead icon="Plus" title="Quick Create" />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[
                { type: 'deals',  icon: 'Target',      label: 'New Deal' },
                { type: 'people', icon: 'User',         label: 'New Person' },
                { type: 'tasks',  icon: 'CheckSquare',  label: 'New Task' },
                { type: 'notes',  icon: 'StickyNote',   label: 'New Note' },
              ].map(item => (
                <button
                  key={item.type}
                  className="nx-btn nx-btn-outline nx-btn-sm"
                  style={{ flex: '1 1 40%' }}
                  onClick={() => store.openCreateForm ? store.openCreateForm(item.type) : toast({ tone: 'info', icon: 'Info', title: 'Use the + button in the sidebar' })}
                >
                  <NexusIcon name={item.icon} size={12} /> {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* Follow-ups */}
          <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: '1px solid var(--nx-border)' }}>
            <SectionHead icon="Clock" title="Follow-ups" count={followups.length} />
            {followups.length === 0 ? (
              <EmptyState icon="Clock" title="No pending follow-ups" desc="Follow-up activities will appear here." compact />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {followups.map(a => {
                  const deal = (store.deals || []).find(d => d.id === a.dealId);
                  const person = (store.people || []).find(p => p.id === a.personId);
                  return (
                    <div key={a.id} style={{ display: 'flex', gap: 8, padding: '6px 8px', background: 'var(--nx-surface)', borderRadius: 6, alignItems: 'center' }}>
                      <div style={{ width: 28, height: 28, borderRadius: 6, background: '#FEF6E6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <NexusIcon name="Clock" size={12} style={{ color: '#B27800' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.what || a.detail || 'Follow-up'}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--nx-text-muted)' }}>
                          {deal ? deal.title : person ? person.name : ''}{a.time ? ' · ' + a.time : ''}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Recently Viewed */}
          <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: '1px solid var(--nx-border)' }}>
            <SectionHead icon="Clock" title="Recently Viewed" count={recent.length} />
            {recent.length === 0 ? (
              <EmptyState icon="History" title="No recent records" desc="Records you view will appear here." compact />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {recent.slice(0, 5).map((r, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', background: 'var(--nx-surface)', borderRadius: 6 }}>
                    <div style={{ width: 26, height: 26, borderRadius: 6, background: 'var(--nx-surface-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <NexusIcon name={recTypeIcon(r.type)} size={12} style={{ color: 'var(--nx-text-muted)' }} />
                    </div>
                    <span style={{ fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name || r.title || 'Record'}</span>
                    <Pill tone="gray">{r.type || 'Record'}</Pill>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Favorites */}
          <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: '1px solid var(--nx-border)' }}>
            <SectionHead icon="Star" title="Favorites" count={favorites.length} />
            {favorites.length === 0 ? (
              <EmptyState icon="Star" title="No favorites" desc="Star records to pin them here for quick access." compact />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {favorites.slice(0, 5).map((r, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', background: 'var(--nx-surface)', borderRadius: 6 }}>
                    <NexusIcon name="Star" size={12} style={{ color: '#F59E0B', flexShrink: 0 }} />
                    <span style={{ fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name || r.title || 'Record'}</span>
                    <Pill tone="gray">{r.type || 'Record'}</Pill>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};
window.SalesWorkspaceView = SalesWorkspaceView;
